// ui.jsx — v4.0 : primitives visuelles partagées (avatars déterministes,
// séparateurs de date, icônes SVG inline style SF Symbols). 100 % local.
import React, { useEffect, useRef, useState } from 'react';
import { isScanSupported, hasNativeDetector } from './qr.js';

// v4.2.1 : version affichée dans les réglages et l'écran d'erreur — permet de
// vérifier D'UN COUP D'ŒIL sur l'iPhone quel build tourne réellement (cache
// Safari / service worker / scp partiel : première cause de « faux bugs »).
// À INCRÉMENTER à chaque livraison, en même temps que CACHE dans sw.js.
export const APP_VERSION = '4.9.4';

// Couleur déterministe dérivée du pseudo/nom (identicon simple).
const PALETTE = ['#0a84ff', '#34c759', '#ff9f0a', '#ff375f', '#bf5af2',
  '#5e5ce6', '#ff6482', '#30b0c7', '#ffd60a', '#ac8e68'];
// v4.3.5 — CORRECTIF : colorFor()/initials() faisaient `name.length` /
// `name.replace(...)` SANS AUCUNE GARDE, alors qu'ils tournent pour CHAQUE
// conversation affichée (ConversationList, en-tête de Chat), sur un `name`
// qui vient de données externes (pseudo de contact, nom de groupe) jamais
// garanties non-vides à ce niveau. Toutes les autres corrections de ce
// projet avaient protégé des APPELANTS individuels (`group.members || []`,
// etc.) mais jamais CES DEUX FONCTIONS À LA SOURCE — un seul contact ou
// groupe avec un champ manquant/non-string suffisait à faire planter TOUTE
// la liste de conversations. Un nom manquant retombe sur '?' plutôt que de
// planter.
export function colorFor(name) {
  const s = typeof name === 'string' && name ? name : '?';
  let h = 0;
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) >>> 0;
  return PALETTE[h % PALETTE.length];
}
function initials(name) {
  const s = typeof name === 'string' && name ? name : '?';
  const parts = s.replace(/^grp:/, '').trim().split(/[\s_-]+/).filter(Boolean);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  return (s.replace(/^grp:/, '')[0] || '?').toUpperCase();
}
export function Avatar({ name, group, size }) {
  return (
    <div className={`avatar ${size === 'sm' ? 'sm' : ''}`}
         style={{ background: group ? 'linear-gradient(135deg,#8e8e93,#636366)' : colorFor(name) }}>
      {group ? '👥' : initials(name)}
    </div>
  );
}

// Séparateurs de date façon Messages iOS.
export function dateLabel(ts) {
  const d = new Date(ts), now = new Date();
  const sameDay = (a, b) => a.toDateString() === b.toDateString();
  if (sameDay(d, now)) return "Aujourd'hui";
  const y = new Date(now); y.setDate(now.getDate() - 1);
  if (sameDay(d, y)) return 'Hier';
  return d.toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' });
}

// Icônes SVG inline, style outline fin (esprit SF Symbols).
const p = { fill: 'none', stroke: 'currentColor', strokeWidth: 1.6, strokeLinecap: 'round', strokeLinejoin: 'round' };
export const Icon = {
  back: () => <svg width="22" height="22" viewBox="0 0 24 24"><path {...p} d="M15 5l-7 7 7 7" /></svg>,
  gear: () => <svg width="22" height="22" viewBox="0 0 24 24" aria-hidden="true"><path {...p} d="M9.7 2.8h4.6l.7 2.3c.5.2 1 .5 1.4.8l2.3-.5 2.3 4-1.6 1.8v1.6l1.6 1.8-2.3 4-2.3-.5c-.4.3-.9.6-1.4.8l-.7 2.3H9.7L9 18.9c-.5-.2-1-.5-1.4-.8l-2.3.5-2.3-4 1.6-1.8v-1.6L3 9.4l2.3-4 2.3.5c.4-.3.9-.6 1.4-.8l.7-2.3z"/><circle {...p} cx="12" cy="12" r="3.1"/></svg>,
  clip: () => <svg width="22" height="22" viewBox="0 0 24 24"><path {...p} d="M8 12l6-6a3 3 0 114 4l-8 8a5 5 0 01-7-7l8-8" /></svg>,
  send: () => <svg width="20" height="20" viewBox="0 0 24 24"><path {...p} d="M4 12l16-8-6 16-3-6-7-2z" /></svg>,
  group: () => <svg width="18" height="18" viewBox="0 0 24 24"><circle {...p} cx="9" cy="8" r="3" /><path {...p} d="M3 20a6 6 0 0112 0M16 6a3 3 0 010 6M21 20a6 6 0 00-4-5.7" /></svg>,
  trash: () => <svg width="18" height="18" viewBox="0 0 24 24"><path {...p} d="M4 7h16M9 7V5h6v2M6 7l1 13h10l1-13" /></svg>,
  mic: () => <svg width="22" height="22" viewBox="0 0 24 24"><rect {...p} x="9" y="3" width="6" height="11" rx="3" /><path {...p} d="M5 11a7 7 0 0014 0M12 18v3M9 21h6" /></svg>,
  micOff: () => <svg width="22" height="22" viewBox="0 0 24 24"><path {...p} d="M9 5v6a3 3 0 004.7 2.5M15 9V6a3 3 0 00-5.2-2M5 11a7 7 0 0011.8 5.1M12 18v3M9 21h6M3 3l18 18" /></svg>,
  phone: () => <svg width="22" height="22" viewBox="0 0 24 24"><path {...p} d="M6.7 3.8l2.6 4.5-2 1.9c1.5 3 3.3 4.8 6.4 6.4l1.9-2 4.5 2.6-.7 3c-.2.8-.9 1.3-1.7 1.3C9.2 21.5 2.5 14.8 2.5 6.3c0-.8.5-1.5 1.3-1.7l2.9-.8z" /></svg>,
  phoneDown: () => <svg width="24" height="24" viewBox="0 0 24 24"><path {...p} d="M5 15.5c4.5-3.5 9.5-3.5 14 0l-2.2 4-3.2-1.5v-2.2a9.5 9.5 0 00-3.2 0V18l-3.2 1.5-2.2-4z" /></svg>,
  video: () => <svg width="22" height="22" viewBox="0 0 24 24"><rect {...p} x="3" y="6" width="12" height="12" rx="2" /><path {...p} d="M15 10l6-3v10l-6-3z" /></svg>,
  videoOff: () => <svg width="22" height="22" viewBox="0 0 24 24"><rect {...p} x="3" y="6" width="12" height="12" rx="2" /><path {...p} d="M15 10l6-3v10l-6-3zM3 3l18 18" /></svg>,
  cameraFlip: () => <svg width="22" height="22" viewBox="0 0 24 24"><path {...p} d="M4 8V5h3M20 16v3h-3M5.5 5.5A8 8 0 0119 9M18.5 18.5A8 8 0 015 15" /><rect {...p} x="8" y="8" width="8" height="8" rx="2" /><circle {...p} cx="12" cy="12" r="2" /></svg>,
  stop: () => <svg width="18" height="18" viewBox="0 0 24 24"><rect x="6" y="6" width="12" height="12" rx="2" fill="currentColor" /></svg>,
  qrcode: () => <svg width="20" height="20" viewBox="0 0 24 24"><rect {...p} x="3" y="3" width="7" height="7" /><rect {...p} x="14" y="3" width="7" height="7" /><rect {...p} x="3" y="14" width="7" height="7" /><path {...p} d="M14 14h3v3h-3zM20 14v3M14 20h3M20 20v.01" /></svg>,
  edit: () => <svg width="16" height="16" viewBox="0 0 24 24"><path {...p} d="M4 20h4L18.5 9.5a2.1 2.1 0 00-3-3L5 17v3z" /></svg>,
  block: () => <svg width="18" height="18" viewBox="0 0 24 24"><circle {...p} cx="12" cy="12" r="9" /><path {...p} d="M6 6l12 12" /></svg>,
};

// v4.1 : affichage du QR encodant pseudo+clé publique — permet de vérifier
// une identité sans comparer un long fingerprint hexadécimal à la main.
// Chargement paresseux de la lib (import dynamique) : elle ne pèse sur le
// bundle que si la fonctionnalité est réellement utilisée.
export function QrCode({ text, size = 220 }) {
  const ref = useRef(null);
  const [error, setError] = useState('');
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const mod = await import('qrcode');
        const QRCode = mod.default || mod;
        if (!cancelled && ref.current) {
          await QRCode.toCanvas(ref.current, text, { width: size, margin: 1 });
        }
      } catch (_) { if (!cancelled) setError('QR code indisponible sur cet appareil.'); }
    })();
    return () => { cancelled = true; };
  }, [text, size]);
  if (error) return <p className="muted small">{error}</p>;
  return <canvas ref={ref} width={size} height={size} className="qr-canvas" />;
}

// v4.1 : lecture QR via la caméra. v4.3 : l'API native BarcodeDetector est
// utilisée quand elle existe (Chrome/Edge) ; PARTOUT AILLEURS (Safari iOS,
// Firefox…) un décodeur bundlé prend le relais — `jsqr`, import dynamique
// (ne pèse sur le bundle téléchargé que si le scan est réellement ouvert),
// décodage 100 % local sur un canvas : aucune image ne quitte l'appareil.
export function QrScanner({ onResult, onClose }) {
  const videoRef = useRef(null);
  const [error, setError] = useState('');
  useEffect(() => {
    if (!isScanSupported()) { setError("Caméra inaccessible sur ce navigateur — comparez le fingerprint affiché à la place."); return undefined; }
    let stream = null, raf = null, stopped = false;
    (async () => {
      try {
        const native = hasNativeDetector() ? new window.BarcodeDetector({ formats: ['qr_code'] }) : null;
        let jsqr = null;
        if (!native) {
          const mod = await import('jsqr');
          jsqr = mod.default || mod;
        }
        stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: 'environment' } });
        if (stopped) { stream.getTracks().forEach((t) => t.stop()); return; }
        videoRef.current.srcObject = stream;
        await videoRef.current.play();
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d', { willReadFrequently: true });
        const tick = async () => {
          if (stopped) return;
          try {
            const v = videoRef.current;
            if (native) {
              const codes = await native.detect(v);
              if (codes[0]?.rawValue) { onResult(codes[0].rawValue); return; }
            } else if (v && v.videoWidth) {
              // Échantillonnage réduit (~480 px de large) : décodage rapide
              // même sur mobile, largement suffisant pour un QR plein cadre.
              const w = 480, h = Math.round((v.videoHeight / v.videoWidth) * w) || 480;
              canvas.width = w; canvas.height = h;
              ctx.drawImage(v, 0, 0, w, h);
              const img = ctx.getImageData(0, 0, w, h);
              const code = jsqr(img.data, w, h, { inversionAttempts: 'dontInvert' });
              if (code?.data) { onResult(code.data); return; }
            }
          } catch (_) { /* trame illisible : on retente */ }
          raf = requestAnimationFrame(tick);
        };
        tick();
      } catch (_) { if (!stopped) setError("Accès à la caméra refusé ou indisponible."); }
    })();
    return () => { stopped = true; if (raf) cancelAnimationFrame(raf); if (stream) stream.getTracks().forEach((t) => t.stop()); };
  }, [onResult]);
  return (
    <div className="sheet-backdrop" onClick={onClose}>
      <div className="sheet" onClick={(e) => e.stopPropagation()}>
        <h1>Scanner un QR code</h1>
        {error
          ? <p className="error">{error}</p>
          : <video ref={videoRef} playsInline muted className="qr-video" />}
        <button className="ghost" onClick={onClose}>Fermer</button>
      </div>
    </div>
  );
}
